import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MerchantPageShell from "../../components/merchant/MerchantPageShell";
import { uploadCsv, getImportRows, commitImport } from "../../lib/importApi";
import { createProduct, getProducts } from "../../lib/catalogApi";

function safeParseJson(value) {
  if (!value) return null;

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function buildPreviewColumns(rows) {
  const preferred = [
    "title",
    "name",
    "brand",
    "category",
    "colour",
    "color",
    "price",
    "sku",
    "image_url",
    "product_url",
  ];

  const keys = new Set();

  rows.forEach((row) => {
    const mapped = safeParseJson(row.mapped_row_json);
    const raw = safeParseJson(row.raw_row_json);

    Object.keys(mapped || {}).forEach((key) => keys.add(key));
    Object.keys(raw || {}).forEach((key) => keys.add(key));
  });

  const orderedPreferred = preferred.filter((key) => keys.has(key));
  const remaining = [...keys].filter((key) => !preferred.includes(key));

  return [...orderedPreferred, ...remaining].slice(0, 6);
}

function getDisplayValue(row, key) {
  const mapped = safeParseJson(row.mapped_row_json);
  const raw = safeParseJson(row.raw_row_json);

  return mapped?.[key] ?? raw?.[key] ?? "—";
}

export default function MerchantImportCsv() {
  const navigate = useNavigate();

  const [selectedSource, setSelectedSource] = useState(null);

  // CSV import state
  const [file, setFile] = useState(null);
  const [importSummary, setImportSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [commitSuccess, setCommitSuccess] = useState(null);
  const [showCommitSuccess, setShowCommitSuccess] = useState(false);

  // Manual add state
  const [form, setForm] = useState({
    title: "",
    brand: "",
    category: "",
    colour: "",
    price: "",
    imageUrl: "",
    productUrl: "",
  });
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Shared
  const [error, setError] = useState("");

  const invalidRows = useMemo(
    () => rows.filter((row) => row.status !== "valid"),
    [rows]
  );

  const previewColumns = useMemo(() => buildPreviewColumns(rows), [rows]);

  async function loadProducts() {
    setLoadingProducts(true);
    try {
      const data = await getProducts();
      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load products", err);
      setError(err?.message || "Could not load products");
    } finally {
      setLoadingProducts(false);
    }
  }

  useEffect(() => {
    loadProducts();
  }, []);

  function resetCsvState() {
    setFile(null);
    setImportSummary(null);
    setRows([]);
    setUploading(false);
    setCommitting(false);
    setCommitSuccess(null);
    setShowCommitSuccess(false);
  }

  function resetManualErrorOnly() {
    setError("");
  }

  function handleChoose(source) {
    setError("");
    setSelectedSource(source);
    setCommitSuccess(null);
    setShowCommitSuccess(false);

    if (source !== "csv") {
      resetCsvState();
    }
  }

  async function handleUpload() {
    if (!file) return;

    setUploading(true);
    setError("");
    setImportSummary(null);
    setRows([]);
    setCommitSuccess(null);
    setShowCommitSuccess(false);

    try {
      const summary = await uploadCsv(file);
      setImportSummary(summary);

      const parsedRows = await getImportRows(summary.id);
      setRows(Array.isArray(parsedRows) ? parsedRows : []);
    } catch (err) {
      console.error("CSV upload failed", err);
      setError(err?.message || "Could not upload CSV");
    } finally {
      setUploading(false);
    }
  }

  async function handleCommit() {
    if (!importSummary) return;

    setCommitting(true);
    setError("");

    try {
      const commit = await commitImport(importSummary.id);

      setCommitSuccess({
        importId: commit.import_id,
        importedCount: commit.imported_count,
        productCount: importSummary.total_rows,
        readyCount: importSummary.valid_rows,
        needsReviewCount: importSummary.invalid_rows,
        sourceLabel: "csv",
        filename: importSummary.filename,
      });

      setShowCommitSuccess(true);
      await loadProducts();
    } catch (err) {
      console.error("Import commit failed", err);
      setError(err?.message || "Could not commit import");
    } finally {
      setCommitting(false);
    }
  }

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleAddProduct(e) {
    e.preventDefault();

    if (!form.title.trim()) {
      setError("Product title is required.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      await createProduct({
        title: form.title.trim(),
        brand: form.brand.trim() || null,
        category: form.category.trim() || null,
        color: form.colour.trim() || null,
        price_amount: form.price.trim() || null,
        currency: "NZD",
        image_url: form.imageUrl.trim() || null,
        product_url: form.productUrl.trim() || null,
      });

      setForm({
        title: "",
        brand: "",
        category: "",
        colour: "",
        price: "",
        imageUrl: "",
        productUrl: "",
      });

      await loadProducts();
    } catch (err) {
      console.error("Failed to create product", err);
      setError(err?.message || "Could not add product");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <MerchantPageShell
      eyebrow="Catalog"
      title="Import products"
      lede="Choose how you want to bring products into Veya. You can switch methods any time."
    >
      {showCommitSuccess && commitSuccess ? (
        <div className="merchant-import-success-overlay">
          <div className="merchant-import-success-card">
            <div className="merchant-import-success-eyebrow">
              Catalog import complete
            </div>

            <h2 className="merchant-import-success-title">
              Your products are ready
            </h2>

            <div className="merchant-import-success-copy">
              Your import has been committed successfully. Go straight to catalog
              manager to review visibility, readiness, and try-on setup.
            </div>

            <div className="merchant-import-success-grid">
              <div className="merchant-import-success-stat">
                <div className="merchant-import-success-stat-label">
                  Products imported
                </div>
                <div className="merchant-import-success-stat-value">
                  {commitSuccess.importedCount || 0}
                </div>
              </div>

              <div className="merchant-import-success-stat">
                <div className="merchant-import-success-stat-label">
                  Rows detected
                </div>
                <div className="merchant-import-success-stat-value">
                  {commitSuccess.productCount || 0}
                </div>
              </div>

              <div className="merchant-import-success-stat">
                <div className="merchant-import-success-stat-label">
                  Valid rows
                </div>
                <div className="merchant-import-success-stat-value">
                  {commitSuccess.readyCount || 0}
                </div>
              </div>

              <div className="merchant-import-success-stat">
                <div className="merchant-import-success-stat-label">
                  Needs review
                </div>
                <div className="merchant-import-success-stat-value">
                  {commitSuccess.needsReviewCount || 0}
                </div>
              </div>
            </div>

            <div className="merchant-import-success-actions">
              <button
                className="gold-btn"
                type="button"
                onClick={() => navigate("/merchant/dashboard/catalog")}
              >
                Go to catalog manager
              </button>

              <button
                className="ghost-btn"
                type="button"
                onClick={() => {
                  setShowCommitSuccess(false);
                  setCommitSuccess(null);
                  resetCsvState();
                  setSelectedSource("csv");
                }}
              >
                Import another file
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <style>{`
        .merchant-import-choice-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }

        .merchant-import-choice-card {
          text-align: left;
          cursor: pointer;
          transition: transform 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease;
          border: 1px solid rgba(0,0,0,0.06);
        }

        .merchant-import-choice-card:hover {
          transform: translateY(-1px);
        }

        .merchant-import-choice-card.is-active {
          border-color: rgba(106, 30, 45, 0.28);
          box-shadow: 0 10px 28px rgba(106, 30, 45, 0.08);
        }

        .merchant-import-source-panel {
          margin-top: 6px;
        }

        .merchant-import-source-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          align-items: center;
          margin-top: 18px;
        }

        .merchant-import-backlink {
          font-size: 14px;
          cursor: pointer;
          color: rgba(40,40,40,0.72);
          transition: color 0.15s ease;
        }

        .merchant-import-backlink:hover {
          color: #6a1e2d;
        }

        .merchant-import-success-overlay {
          position: fixed;
          inset: 0;
          background: rgba(20, 16, 16, 0.38);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          z-index: 1000;
        }

        .merchant-import-success-card {
          width: min(560px, 100%);
          border-radius: 28px;
          background: rgba(250, 246, 242, 0.98);
          box-shadow: 0 28px 80px rgba(20, 16, 16, 0.22);
          padding: 28px;
          border: 1px solid rgba(106, 30, 45, 0.08);
        }

        .merchant-import-success-eyebrow {
          font-size: 12px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: rgba(106, 30, 45, 0.72);
          margin-bottom: 8px;
          font-weight: 700;
        }

        .merchant-import-success-title {
          font-size: 28px;
          line-height: 1.1;
          margin: 0 0 10px;
          color: rgba(20,20,20,0.94);
        }

        .merchant-import-success-copy {
          font-size: 15px;
          line-height: 1.6;
          color: rgba(40,40,40,0.72);
          margin-bottom: 18px;
        }

        .merchant-import-success-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
          margin-bottom: 20px;
        }

        .merchant-import-success-stat {
          padding: 14px 16px;
          border-radius: 18px;
          background: rgba(0,0,0,0.03);
        }

        .merchant-import-success-stat-label {
          font-size: 12px;
          color: rgba(40,40,40,0.58);
          margin-bottom: 6px;
        }

        .merchant-import-success-stat-value {
          font-size: 20px;
          font-weight: 600;
          color: rgba(20,20,20,0.92);
        }

        .merchant-import-success-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          align-items: center;
        }

        @media (max-width: 980px) {
          .merchant-import-choice-grid {
            grid-template-columns: 1fr;
          }

          .merchant-import-success-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="merchant-import-choice-grid">
        <button
          type="button"
          className={`public-card merchant-import-choice-card ${
            selectedSource === "shopify" ? "is-active" : ""
          }`}
          onClick={() => handleChoose("shopify")}
        >
          <div className="public-card-title">Connect Shopify</div>
          <div className="public-card-sub">
            Best long-term path. Keep this as the dedicated integration route once Shopify is ready.
          </div>
        </button>

        <button
          type="button"
          className={`public-card merchant-import-choice-card ${
            selectedSource === "csv" ? "is-active" : ""
          }`}
          onClick={() => handleChoose("csv")}
        >
          <div className="public-card-title">Upload CSV</div>
          <div className="public-card-sub">
            Import your catalog from a spreadsheet export, review parsed rows, then commit valid products.
          </div>
        </button>

        <button
          type="button"
          className={`public-card merchant-import-choice-card ${
            selectedSource === "manual" ? "is-active" : ""
          }`}
          onClick={() => handleChoose("manual")}
        >
          <div className="public-card-title">Add products manually</div>
          <div className="public-card-sub">
            Best for small boutiques or early testing. Add a few products first, then return to catalog.
          </div>
        </button>
      </div>

      {error ? <div className="merchant-error">{error}</div> : null}

      {!selectedSource ? (
        <div className="public-card">
          <div className="public-card-title">Choose an import method</div>
          <div className="public-card-sub">
            Select one of the options above to continue on this page.
          </div>
        </div>
      ) : null}

      {selectedSource === "shopify" ? (
        <div className="merchant-import-source-panel">
          <div className="public-card">
            <div className="public-card-title">Shopify connection</div>
            <div className="public-card-sub">
              This will become the dedicated Shopify import path. For now, use CSV or manual entry while the integration is being completed.
            </div>

            <div className="merchant-import-source-actions">
              <button
                className="ghost-btn"
                type="button"
                onClick={() => handleChoose("csv")}
              >
                Use CSV for now
              </button>

              <button
                className="ghost-btn"
                type="button"
                onClick={() => handleChoose("manual")}
              >
                Add manually instead
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {selectedSource === "csv" ? (
        <div className="merchant-import-source-panel">
          <div className="public-section">
            <div className="public-card">
              <div className="public-card-title">CSV import</div>
              <div className="public-card-sub">
                Upload your file, review parsed rows, then commit the valid products.
              </div>
            </div>
          </div>

          <div className="public-section">
            <label className="merchant-field">
              <span className="merchant-label">Catalog file</span>
              <input
                className="merchant-input"
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => {
                  setFile(e.target.files?.[0] || null);
                  setError("");
                }}
              />
            </label>

            {file ? (
              <div className="public-card" style={{ marginTop: 12 }}>
                <div className="public-card-title">Selected file</div>
                <div className="public-card-sub">{file.name}</div>
              </div>
            ) : null}

            <div className="merchant-actions">
              <button
                className="gold-btn"
                type="button"
                onClick={handleUpload}
                disabled={!file || uploading}
              >
                {uploading ? "Uploading..." : "Upload and parse CSV"}
              </button>
            </div>
          </div>

          {importSummary ? (
            <>
              <div className="public-section">
                <div className="public-pillbox-title">Import summary</div>

                <div className="public-grid">
                  <div className="public-card">
                    <div className="public-card-title">Filename</div>
                    <div className="public-card-sub">{importSummary.filename}</div>
                  </div>

                  <div className="public-card">
                    <div className="public-card-title">Rows detected</div>
                    <div className="public-card-sub">{importSummary.total_rows}</div>
                  </div>

                  <div className="public-card">
                    <div className="public-card-title">Valid rows</div>
                    <div className="public-card-sub">{importSummary.valid_rows}</div>
                  </div>

                  <div className="public-card">
                    <div className="public-card-title">Invalid rows</div>
                    <div className="public-card-sub">{importSummary.invalid_rows}</div>
                  </div>
                </div>
              </div>

              <div className="public-section">
                <div className="public-pillbox-title">Parsed row preview</div>

                {!rows.length ? (
                  <div className="public-card">
                    <div className="public-card-title">No rows returned</div>
                    <div className="public-card-sub">
                      The import summary was created, but no row preview data was returned.
                    </div>
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table className="merchant-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Status</th>
                          {previewColumns.map((column) => (
                            <th key={column}>{column}</th>
                          ))}
                          <th>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.slice(0, 12).map((row) => (
                          <tr key={row.id}>
                            <td>{row.row_number}</td>
                            <td>{row.status}</td>
                            {previewColumns.map((column) => (
                              <td key={column}>{String(getDisplayValue(row, column))}</td>
                            ))}
                            <td>{row.error_message || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {rows.length > 12 ? (
                  <div className="public-card" style={{ marginTop: 12 }}>
                    <div className="public-card-sub">
                      Showing the first 12 rows of {rows.length}.
                    </div>
                  </div>
                ) : null}
              </div>

              {invalidRows.length ? (
                <div className="public-section">
                  <div className="public-pillbox-title">Validation issues</div>

                  <div className="public-grid">
                    {invalidRows.slice(0, 6).map((row) => (
                      <div className="public-card" key={row.id}>
                        <div className="public-card-title">Row {row.row_number}</div>
                        <div className="public-card-sub">
                          {row.error_message || "This row could not be imported."}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="merchant-actions">
                <button
                  className="gold-btn"
                  type="button"
                  onClick={handleCommit}
                  disabled={committing || importSummary.valid_rows < 1}
                >
                  {committing ? "Committing import..." : "Commit valid products"}
                </button>
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      {selectedSource === "manual" ? (
        <div className="merchant-import-source-panel">
          <form className="merchant-form" onSubmit={handleAddProduct}>
            <label className="merchant-field">
              <span className="merchant-label">Product title</span>
              <input
                className="merchant-input"
                value={form.title}
                onChange={(e) => updateField("title", e.target.value)}
                placeholder="Oversized Wool Coat"
              />
            </label>

            <label className="merchant-field">
              <span className="merchant-label">Brand</span>
              <input
                className="merchant-input"
                value={form.brand}
                onChange={(e) => updateField("brand", e.target.value)}
                placeholder="Atelier Edit"
              />
            </label>

            <label className="merchant-field">
              <span className="merchant-label">Category</span>
              <input
                className="merchant-input"
                value={form.category}
                onChange={(e) => updateField("category", e.target.value)}
                placeholder="outer"
              />
            </label>

            <label className="merchant-field">
              <span className="merchant-label">Colour</span>
              <input
                className="merchant-input"
                value={form.colour}
                onChange={(e) => updateField("colour", e.target.value)}
                placeholder="charcoal"
              />
            </label>

            <label className="merchant-field">
              <span className="merchant-label">Price</span>
              <input
                className="merchant-input"
                value={form.price}
                onChange={(e) => updateField("price", e.target.value)}
                placeholder="299"
              />
            </label>

            <label className="merchant-field">
              <span className="merchant-label">Image URL</span>
              <input
                className="merchant-input"
                value={form.imageUrl}
                onChange={(e) => updateField("imageUrl", e.target.value)}
                placeholder="https://example.com/product.jpg"
              />
            </label>

            <label className="merchant-field">
              <span className="merchant-label">Product URL</span>
              <input
                className="merchant-input"
                value={form.productUrl}
                onChange={(e) => updateField("productUrl", e.target.value)}
                placeholder="https://yourstore.com/products/item"
              />
            </label>

            <div className="merchant-actions">
              <button
                className="ghost-btn"
                type="submit"
                disabled={submitting}
                onClick={resetManualErrorOnly}
              >
                {submitting ? "Adding..." : "Add product"}
              </button>

              <button
                className="gold-btn"
                type="button"
                onClick={() => navigate("/merchant/dashboard/catalog")}
              >
                Back to catalog manager
              </button>
            </div>
          </form>

          <div className="public-section">
            <div className="public-pillbox-title">Saved products</div>

            {loadingProducts ? (
              <div className="public-card">
                <div className="public-card-title">Loading products</div>
                <div className="public-card-sub">Fetching your catalog.</div>
              </div>
            ) : !products.length ? (
              <div className="public-card">
                <div className="public-card-title">No products yet</div>
                <div className="public-card-sub">
                  Add at least one product to begin building your catalog.
                </div>
              </div>
            ) : (
              <div className="public-grid">
                {products.map((product) => (
                  <div className="public-card" key={product.id}>
                    <div className="public-card-title">
                      {product.title || "Untitled product"}
                    </div>
                    <div className="public-card-sub">
                      {product.category || "No category"} • {product.color || "No colour"} •{" "}
                      {product.price_amount || "No price"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </MerchantPageShell>
  );
}